/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dialog } from '@headlessui/react';
import React from 'react';
import ImageUploading, { ErrorsType, ImageListType } from 'react-images-uploading';
import { v4 as uuidv4 } from 'uuid';

import { postDeleteImages } from '../api/POST_DeleteImages';
import { postUploadImage } from '../api/POST_UploadImage';
import { resizeFile } from '../utils/resizeFile';
import Modal from './Modal';
import { ModalUploadImageProps } from './ModalUploadImage.type';

function readFileAsync(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result as string);
    };

    reader.onerror = reject;

    reader.readAsDataURL(file);
  });
}

const ModalUploadImage = (props: ModalUploadImageProps) => {
  const [currentImages, setCurrentImages] = React.useState<ImageListType>(
    props.product.images || null
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isMaxNumber, setIsMaxNumber] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (props.product.images === null) {
      setCurrentImages(null);
    } else {
      setCurrentImages(props.product.images);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.showModal]);

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const images = currentImages?.filter((item) => !item.uuid).map((img) => img.file);
      const uuid = images.map(() => uuidv4());
      const formData = new FormData();
      for (const id of uuid) {
        formData.append('uuid[]', id);
      }
      for (const image of images) {
        formData.append('images', image);
      }
      formData.append('sku', props.sku);
      formData.append('brandName', props.brandName || '-');
      formData.append('outletName', props.outletName);
      formData.append('category', props.category);

      if (images.length > 0) {
        const { data, status } = await postUploadImage(formData);
        if (status === 'success') {
          const uploadedImagesBefore = currentImages?.filter((item) => item.uuid);
          for await (const [index, image] of data.images.entries()) {
            const dataURL = await readFileAsync(images[index]);
            uploadedImagesBefore.push({ ...image, dataURL });
          }
          console.log(uploadedImagesBefore);
          setCurrentImages(uploadedImagesBefore);
          props.handleProductChange(props.currentIndex, uploadedImagesBefore, 'images');
        }
      }
    } catch (error) {
      return error;
    } finally {
      setIsLoading(false);
      props.closeModal();
    }
  };

  const onChangeImage = (images: ImageListType) => {
    Promise.all(
      images.map(async (item) => {
        const file = await resizeFile(item.file);
        return {
          ...item,
          file
        };
      })
    ).then((newImages) => {
      setCurrentImages(newImages);
      setIsMaxNumber(false);
      props.handleProductChange(props.currentIndex, newImages, 'images');
    });
  };

  const onCancel = () => {
    props.closeModal();
    //const images = currentImages?.filter((item) => item.uuid);
    // setCurrentImages(images);
    // setIsMaxNumber(false);
    // props.handleProductChange(props.currentIndex, images, 'images');
  };

  const onError = (errors: ErrorsType) => {
    if (errors?.maxNumber) {
      setIsMaxNumber(true);
    }
  };

  return (
    <Modal
      cancelButtonRef={props.cancelButtonRef}
      isShowModal={props.showModal}
      closeModalBackground={props.closeModalBackground}>
      <div className="mb-6">
        <Dialog.Title
          as="div"
          className="text-Large text-gray-gray700 font-interSemiBold text-center  mb-4">
          Tambahkan Foto Produk
        </Dialog.Title>
        <Dialog.Description
          as="div"
          className="text-regular text-gray-gray700 font-interLight text-center">
          Pilih satu atau beberapa foto dari perangkat
        </Dialog.Description>
        <Dialog.Description
          as="div"
          className="text-regular text-gray-gray700 font-interSemiBold text-center mb-4">
          (maksimal 10 foto)
        </Dialog.Description>
        <div className="mb-6">
          <ImageUploading
            multiple
            value={currentImages}
            onChange={onChangeImage}
            maxNumber={10}
            resolutionType="ratio"
            onError={onError}>
            {({ imageList, onImageRemove, onImageUpload }) => {
              return (
                <div>
                  {imageList.length > 0 ? (
                    <div className="flex flex-wrap">
                      {imageList.map((image, index) => (
                        <div key={index} className="relative mr-3.5 mb-3.5">
                          <img
                            src={image.dataURL}
                            alt={`image${index}`}
                            className="w-14 h-14 rounded-lg"
                          />
                          <div className="absolute -top-1.5 -right-1.5">
                            <button
                              onClick={() => {
                                if (image.uuid) {
                                  postDeleteImages({ uuid: [image.uuid] });
                                }
                                onImageRemove(index);
                                const filterImage = currentImages.filter((img) => img !== image);
                                setCurrentImages(filterImage);
                                props.handleProductChange(
                                  props.currentIndex,
                                  filterImage,
                                  'images'
                                );
                              }}>
                              <img src="/images/close.svg" alt="Close icon" />
                            </button>
                          </div>
                        </div>
                      ))}
                      <div
                        className="w-14 h-14 border-1.5 flex justify-center items-center border-dashed rounded-lg border-gray-gray700 p-4"
                        onClick={onImageUpload}>
                        <img src="/images/plus.svg" alt="Plus icon" />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div
                        className="w-full h-24.5 items-center border border-dashed rounded-lg border-gray-gray700 p-6"
                        onClick={onImageUpload}>
                        <div className="flex justify-center items-center mb-1">
                          <div className="text-regular text-primary-green font-interSemiBold mr-2">
                            Tambahkan foto
                          </div>
                          <img src="/images/plus.svg" alt="Plus icon" />
                        </div>
                        <div className="text-small text-gray-gray600 font-interLight text-center">
                          Ketuk pada area untuk menambahkan
                        </div>
                      </div>
                    </>
                  )}
                  {isMaxNumber && (
                    <div className="flex justify-center items-center mt-4">
                      <img src="/images/crossRed.svg" className="mr-1" />
                      <div className="text-small text-danger-default font-interSemiBold">
                        Maksimal foto yang dapat disimpan adalah 10 foto
                      </div>
                    </div>
                  )}
                </div>
              );
            }}
          </ImageUploading>
        </div>
      </div>
      <div className="flex space-x-4">
        <button
          type="button"
          className="w-1/2 p-4 text-center border-2 border-primary-green bg-white rounded-lg text-primary-green font-interSemiBold focus:outline-none focus:ring-transparent"
          onClick={onCancel}
          ref={props.cancelButtonRef}>
          Batal
        </button>
        <button
          disabled={!currentImages ? true : false}
          type="button"
          className={
            !currentImages
              ? 'w-1/2 p-4 flex justify-center items-center text-center rounded-lg bg-gray-gray300 focus:outline-none focus:ring-transparent focus:ring-primary-green cursor-default'
              : 'w-1/2 p-4 flex justify-center items-center text-center rounded-lg bg-primary-green focus:outline-none focus:ring-transparent focus:ring-primary-green'
          }
          onClick={onSubmit}>
          {isLoading ? (
            <div>
              <img src="/images/spinner.gif" className="w-6 h-6" />
            </div>
          ) : (
            <div
              className={
                !currentImages
                  ? 'text-large text-gray-gray500 font-interSemiBold'
                  : 'text-large text-white font-interSemiBold'
              }>
              Simpan
            </div>
          )}
        </button>
      </div>
    </Modal>
  );
};

export default ModalUploadImage;
